import { useEffect } from 'react'
import { Alert, Form, Input, Modal, Select, Typography } from 'antd'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { ticketTransition, type Ticket } from './crmApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

/**
 * "Yopish" — SABAB bilan.
 *
 * <h2>Nega bu modal kerak bo'ldi (tester A5)</h2>
 * `TicketsPage` yopish tugmasini boshqa o'tishlar bilan BIR XIL
 * ko'rgan va tanasiz yuborardi. Backend esa (`TicketServiceImpl.close`)
 * sababsiz so'rovni rad etadi — ya'ni murojaatni umuman yopib
 * bo'lmasdi, har bosishda 400 qaytardi. Bu `LeadLostModal` yopgan
 * nuqsonning AYNAN nusxasi: bir xil naqsh, ikkinchi ekranda.
 *
 * <h2>⚠️ Nega yechim "sababni ixtiyoriy qilish" EMAS</h2>
 * Sabab BAZA invarianti, xizmat qatlamining injiqligi emas:
 *
 * <pre>
 *   CONSTRAINT ticket_closed_consistent CHECK (
 *       (status = 'CLOSED' AND closed_at IS NOT NULL
 *                         AND close_reason IS NOT NULL) OR ...)
 * </pre>
 *
 * Ya'ni backendda `reason` ni ixtiyoriy qilsak, 400 yo'qolmasdi —
 * u faqat KECHROQ va BATTAR shaklda qaytardi: aniq xato o'rniga
 * `CONSTRAINT_23514` tegi bilan cheklov buzilishi. Xatoni chuqurroq
 * qatlamga surish — tuzatish emas.
 *
 * <h2>⚠️ Tayyor sabablar + erkin izoh — ikkalasi ham</h2>
 * `LeadLostModal` dagi bilan bir xil qaror: faqat erkin matn qolsa har
 * operator o'zicha yozadi va yopilish sabablari bo'yicha hisobot
 * chiqmaydi; faqat ro'yxat qolsa haqiqiy sabab "Boshqa" ichida
 * yo'qoladi.
 */
interface TicketCloseModalProps {
  /** `null` — modal yopiq */
  ticket: Ticket | null
  onCancel: () => void
  onDone: () => void
}

/** Kalitlar — i18n uchun; serverga TARJIMA QILINGAN matn ketadi. */
const REASONS = [
  'SOLVED',
  'DUPLICATE',
  'NO_RESPONSE',
  'WITHDRAWN',
  'NOT_OUR_SCOPE',
  'OTHER',
] as const

interface CloseValues {
  reasonCode: (typeof REASONS)[number]
  note?: string
}

export function TicketCloseModal({ ticket, onCancel, onDone }: TicketCloseModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<CloseValues>()

  useEffect(() => {
    if (ticket) form.resetFields()
  }, [ticket, form])

  const closing = useMutation({
    mutationFn: (reason: string) => ticketTransition(ticket!.id, 'close', { reason }),
    onSuccess: onDone,
  })

  /**
   * Bekor qilib chiqish — xato xabari ham qolmasin: keyingi murojaat
   * uchun u eski va begona bo'lardi.
   *
   * ⚠️ Xato mutatsiyaning O'ZIDA turadi (`LeadLostModal` naqshi),
   * alohida `useState` da emas.
   */
  const dismiss = () => {
    closing.reset()
    onCancel()
  }

  return (
    <Modal
      open={ticket !== null}
      title={t('crm.ticketClose.title')}
      onCancel={dismiss}
      onOk={() =>
        submitOnValid(form, (values) => {
          // ⚠️ Sabab MATN sifatida saqlanadi (`ticket.close_reason` —
          // TEXT). Kod emas, chunki baza uni ro'yxat sifatida bilmaydi;
          // izoh esa tanlangan sababdan KEYIN qo'shiladi, uni
          // almashtirmaydi (`LeadLostModal` dagi bir xil qaror).
          const label = t(`crm.ticketClose.reason.${values.reasonCode}`)
          const note = values.note?.trim()
          closing.mutate(note ? `${label} — ${note}` : label)
        })
      }
      confirmLoading={closing.isPending}
      okText={t('crm.ticketClose.confirm')}
      cancelText={t('common.cancel')}
      destroyOnHidden
    >
      {ticket ? (
        <Typography.Paragraph type="secondary">
          {t('crm.ticketClose.subtitle', { subject: ticket.subject })}
        </Typography.Paragraph>
      ) : null}

      {closing.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(closing.error, t)}
        />
      ) : null}

      <Form<CloseValues> form={form} layout="vertical" preserve={false}>
        <Form.Item
          name="reasonCode"
          label={t('crm.ticketClose.reasonLabel')}
          rules={[{ required: true, message: t('crm.ticketClose.reasonRequired') }]}
        >
          <Select
            autoFocus
            options={REASONS.map((code) => ({
              value: code,
              label: t(`crm.ticketClose.reason.${code}`),
            }))}
          />
        </Form.Item>
        <Form.Item
          name="note"
          label={t('crm.ticketClose.note')}
          extra={t('crm.ticketClose.noteHint')}
        >
          <Input.TextArea rows={3} maxLength={500} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
